import dayjs from 'dayjs';
import { FC, useMemo } from 'react';
import { Steps, Typography } from 'antd';
import { FaBell, FaCheckDouble } from 'react-icons/fa';
import { AiFillLike } from 'react-icons/ai';
import { HiOutlineInboxIn } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { IOrderSearch } from '../../../../interfaces/requests.interface';

type DriverSearchTimelineProps = {
    metadata: IOrderSearch['metadata'];
};

const { Text } = Typography;

type StepTextProps = {
    isActive: boolean;
    value: string;
};

const StepText: FC<StepTextProps> = ({ isActive, value }) => {
    const styles = {
        fontSize: 12,
        color: isActive ? 'var(--color-purple)' : 'var(--color-gray-1)',
    };

    return (
        <Text style={styles}>
            {value}
        </Text>
    );
};

const DriverSearchTimeline: FC<DriverSearchTimelineProps> = ({ metadata }) => {
    const { t } = useTranslation('small-fleet-orders');

    const formatDate = (dateStr?: string) => (
        dateStr ? dayjs(dateStr).format('HH:mm') : ''
    );

    const steps = useMemo(() => ([
        {
            key: 'wasNotified',
            title: (
                <StepText
                    isActive={metadata.wasNotified}
                    value={t('SEARCH_NOTIFIED')}
                />
            ),
            isDone: Boolean(metadata.wasNotified),
            date: (
                <StepText
                    isActive={metadata.wasNotified}
                    value={formatDate(metadata.wasNotifiedAt)}
                />
            ),
            icon: <FaBell size={16} style={{ verticalAlign: 'baseline' }} />,
        },
        {
            key: 'wasReceived',
            title: (
                <StepText
                    isActive={metadata.wasReceived}
                    value={t('SEARCH_RECEIVED')}
                />
            ),
            isDone: Boolean(metadata.wasReceived),
            date: (
                <StepText
                    isActive={metadata.wasReceived}
                    value={formatDate(metadata.wasReceivedAt)}
                />
            ),
            icon: <HiOutlineInboxIn size={16} style={{ verticalAlign: 'baseline' }} />,
        },
        {
            key: 'wasRead',
            title: (
                <StepText
                    isActive={metadata.wasRead}
                    value={t('SEARCH_READ')}
                />
            ),
            isDone: Boolean(metadata.wasRead),
            date: (
                <StepText
                    isActive={metadata.wasRead}
                    value={formatDate(metadata.wasReadAt)}
                />
            ),
            icon: <FaCheckDouble size={16} style={{ verticalAlign: 'baseline' }} />,
        },
        {
            key: 'wasAccepted',
            title: (
                <StepText
                    isActive={metadata.wasAccepted}
                    value={t('SEARCH_ACCEPTED')}
                />
            ),
            isDone: Boolean(metadata.wasAccepted),
            date: (
                <StepText
                    isActive={metadata.wasAccepted}
                    value={formatDate(metadata.wasAcceptedAt)}
                />
            ),
            icon: <AiFillLike size={16} style={{ verticalAlign: 'baseline' }} />,
        },
    ]), [
        metadata.wasNotified,
        metadata.wasNotifiedAt,
        metadata.wasReceived,
        metadata.wasReceivedAt,
        metadata.wasRead,
        metadata.wasReadAt,
        metadata.wasAccepted,
        metadata.wasAcceptedAt,
        t,
    ]);

    return (
        <Steps
            size="small"
            items={steps.map((step) => ({
                title: step.title,
                description: step.date || undefined,
                status: step.isDone ? 'finish' : 'wait',
                disabled: !step.isDone,
                icon: step.icon,
            }))}
        />
    );
};

export { DriverSearchTimeline };
